import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyCarcassReconciliation,
  createFieldMapping,
  fetchCarcassReconciliation,
  fetchEidAssignments,
  fetchFieldMappingsMeta,
  fetchUnmappedFields,
  reviewEidAssignment,
  type CarcassReconciliation,
  type EidAssignment,
  type UnmappedField,
} from '../data/api';

const CARD = 'rounded-xl border border-[#1e6bb829] bg-white';
const HEAD = 'flex items-baseline justify-between gap-2 border-b border-[#1e6bb824] bg-[#f8fbff] px-4 py-2';
const LABEL = 'text-sm font-semibold uppercase tracking-wide text-avcs-muted';
const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted';
const ERROR_BOX = 'mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]';
const OK_BOX = 'mb-3 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]';
const WARN = 'rounded-lg border border-[#e7d5b9] bg-[#fbf4e7] px-3 py-2 text-xs text-[#6b4710]';

function fmt(value?: string | null): string {
  return value == null || value === '' ? '—' : value;
}

export type DealNeedsReviewProps = {
  dealId: string;
  /** Event types present on this deal's uploads, to scope the column inbox. */
  eventTypes?: string[];
  onChanged?: () => void;
};

export default function DealNeedsReview({ dealId, eventTypes, onChanged }: DealNeedsReviewProps) {
  const [claims, setClaims] = useState<EidAssignment[]>([]);
  const [carcasses, setCarcasses] = useState<CarcassReconciliation | null>(null);
  const [columns, setColumns] = useState<UnmappedField[]>([]);
  const [targetFields, setTargetFields] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // animal_id -> row_id
  const [pairing, setPairing] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    const [claimsRes, carcassRes, colsRes] = await Promise.allSettled([
      fetchEidAssignments({ deal: dealId, status: 'PENDING' }),
      fetchCarcassReconciliation(dealId),
      Promise.all(
        (eventTypes && eventTypes.length ? eventTypes : []).map((t) => fetchUnmappedFields({ event_type: t })),
      ),
    ]);
    setClaims(claimsRes.status === 'fulfilled' ? claimsRes.value : []);
    setCarcasses(carcassRes.status === 'fulfilled' ? carcassRes.value : null);
    if (colsRes.status === 'fulfilled') {
      const seen = new Set<string>();
      setColumns(colsRes.value.flat().filter((c) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      }));
    } else {
      setColumns([]);
    }
    setLoading(false);
  }, [dealId, eventTypes]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchFieldMappingsMeta().then((m) => setTargetFields(m.target_fields || [])).catch(() => setTargetFields([]));
  }, []);

  // Pre-pair one-to-one, which is the common case: one untagged beast, one
  // untagged carcass. Never guess when the counts don't line up.
  useEffect(() => {
    if (!carcasses?.can_pair_all) return;
    const next: Record<string, string> = {};
    carcasses.unaccounted_animals.forEach((a, i) => {
      const row = carcasses.unmatched_carcasses[i];
      if (row) next[a.id] = row.row_id;
    });
    setPairing(next);
  }, [carcasses]);

  const after = async (message: string) => {
    setNotice(message);
    await load();
    onChanged?.();
  };

  const decideClaim = async (claim: EidAssignment, action: 'confirm' | 'revert') => {
    setBusy(claim.id);
    setError('');
    try {
      await reviewEidAssignment(claim.id, action);
      setClaims((prev) => prev.filter((c) => c.id !== claim.id));
      setNotice(action === 'confirm' ? 'Merge confirmed.' : 'Merge undone — the identifier was handed back.');
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Could not record that decision.');
    } finally {
      setBusy('');
    }
  };

  const chosenPairs = useMemo(
    () => Object.entries(pairing)
      .filter(([, rowId]) => rowId)
      .map(([animal_id, row_id]) => ({ animal_id, row_id })),
    [pairing],
  );

  const applyPairs = async () => {
    if (!chosenPairs.length) return;
    setBusy('pairs');
    setError('');
    setNotice('');
    try {
      const result = await applyCarcassReconciliation(dealId, { pairs: chosenPairs });
      if (result.errors?.length) setError(result.errors.map((e) => e.detail).join(' '));
      await after(`${result.paired.length} carcass(es) paired using their own measured weights.`);
    } catch (e: any) {
      setError(e?.message || 'Could not apply the pairing.');
    } finally {
      setBusy('');
    }
  };

  const estimate = async (animalId: string) => {
    setBusy(animalId);
    setError('');
    setNotice('');
    try {
      const result = await applyCarcassReconciliation(dealId, { estimate_animal_ids: [animalId] });
      if (result.errors?.length) setError(result.errors.map((e) => e.detail).join(' '));
      if (result.estimated?.length) {
        await after('Estimated from the deal average, flagged as an estimate and excluded from dressing percentage.');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not estimate.');
    } finally {
      setBusy('');
    }
  };

  const mapColumn = async (field: UnmappedField, target: string) => {
    if (!target) return;
    setBusy(field.id);
    setError('');
    try {
      await createFieldMapping({
        event_type: field.event_type, source_field: field.source_field, target_field: target,
      });
      await after(`“${field.source_field}” now maps to ${target}. It applies from the next upload — use the field-mapping editor's re-apply to fix rows already ingested.`);
    } catch (e: any) {
      setError(e?.message || 'Could not create the mapping.');
    } finally {
      setBusy('');
    }
  };

  const unaccounted = carcasses?.unaccounted_animals ?? [];
  const unmatched = carcasses?.unmatched_carcasses ?? [];
  const total = claims.length + unaccounted.length + columns.length;

  return (
    <div className="grid gap-4">
      <div>
        <h3 className="text-lg font-semibold text-avcs-text">Needs review</h3>
        <p className="mt-0.5 text-sm text-avcs-muted">
          Everything the ingest could not decide on its own. Left unresolved, each of these is a figure that is
          quietly wrong somewhere else on this deal.
        </p>
      </div>

      {error && <div role="alert" className={ERROR_BOX}>{error}</div>}
      {notice && <div className={OK_BOX}>{notice}</div>}

      {loading ? (
        <p className="text-sm text-avcs-muted">Loading…</p>
      ) : total === 0 ? (
        <p className={`${CARD} p-4 text-sm text-avcs-muted`}>
          Nothing needs review on this deal.
        </p>
      ) : null}

      {/* 1. Untagged carcasses ------------------------------------------- */}
      {!loading && unaccounted.length > 0 && (
        <div className={CARD}>
          <div className={HEAD}>
            <span className={LABEL}>Animals with no slaughter record</span>
            <span className="text-xs text-avcs-muted">{unaccounted.length} animal(s) · {unmatched.length} unmatched carcass(es)</span>
          </div>
          <div className="p-4">
            <p className="mb-3 text-xs text-avcs-muted">
              The abattoir weighs every beast, tagged or not. A row whose tag column reads <code>NOTAG</code> carries a
              real, measured carcass — it just has nothing to match on. Pair it here and the animal keeps its own
              weights. Estimating is the fallback for animals with no carcass left to pair, and anything estimated is
              excluded from the dressing percentage.
            </p>

            {carcasses?.can_pair_all && (
              <div className={`${WARN} mb-3`}>
                Counts match — every animal can be paired with a measured carcass. Nothing needs estimating.
              </div>
            )}

            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
                  <tr>
                    <th className={TH}>Animal</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Pair with carcass</th>
                    <th className={TH}>Or</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e6bb812]">
                  {unaccounted.map((animal) => (
                    <tr key={animal.id} className="hover:bg-[#f8fbff]">
                      <td className="px-3 py-2">
                        <span className="font-mono text-avcs-text">{fmt(animal.eid || animal.vid)}</span>
                        {!animal.eid && !animal.vid && <span className="text-avcs-muted">untagged</span>}
                      </td>
                      <td className="px-3 py-2 text-avcs-muted">
                        {fmt(animal.status)}
                        {animal.current_entity ? ` · ${animal.current_entity}` : ''}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className="rounded-lg border border-[#1e6bb833] px-2 py-1"
                          value={pairing[animal.id] || ''}
                          onChange={(e) => setPairing((p) => ({ ...p, [animal.id]: e.target.value }))}
                        >
                          <option value="">Choose a carcass…</option>
                          {unmatched.map((row) => (
                            <option key={row.row_id} value={row.row_id}>
                              row {row.row_number} · SN {fmt(row.sequence_number)} · CCM {fmt(row.ccm)} · live {fmt(row.live_weight)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="btn"
                          disabled={busy !== '' || unmatched.length > 0}
                          title={unmatched.length > 0
                            ? 'Pair it with a measured carcass instead — estimating is only for animals with none left'
                            : 'Create an estimated record from the deal average'}
                          onClick={() => estimate(animal.id)}
                        >Estimate</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <button
                type="button"
                className="btn admin-submit-action"
                disabled={busy !== '' || chosenPairs.length === 0}
                onClick={applyPairs}
              >
                {busy === 'pairs' ? 'Applying…' : `Pair ${chosenPairs.length} carcass(es)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. EID / identifier claims -------------------------------------- */}
      {!loading && claims.length > 0 && (
        <div className={CARD}>
          <div className={HEAD}>
            <span className={LABEL}>Identifiers matched to existing animals</span>
            <span className="text-xs text-avcs-muted">{claims.length} to confirm</span>
          </div>
          <div className="p-4">
            <p className="mb-3 text-xs text-avcs-muted">
              A later file matched one of this deal's animals on a secondary identifier and gave it another one —
              two identities becoming one. Usually right, and it is what keeps a tagless registration traceable.
              Confirm each, or undo it to hand the identifier back.
            </p>
            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
                  <tr>
                    <th className={TH}>Gained</th>
                    <th className={TH}>Matched on</th>
                    <th className={TH}>Source file</th>
                    <th className={TH}>Decision</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e6bb812]">
                  {claims.map((claim) => (
                    <tr key={claim.id} className="hover:bg-[#f8fbff]">
                      <td className="px-3 py-2">
                        <span className="text-avcs-muted">{claim.claimed_field}</span>{' '}
                        <span className="font-mono text-avcs-text">{fmt(claim.claimed_value || claim.eid)}</span>
                      </td>
                      <td className="px-3 py-2 text-avcs-muted">
                        {claim.matched_on === 'VID' ? 'Visual ID' : claim.matched_on === 'EID' ? 'EID' : 'Other ID'}
                        {claim.matched_value ? ` · ${claim.matched_value}` : ''}
                      </td>
                      <td className="px-3 py-2 text-xs text-avcs-muted">{fmt(claim.session_label)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button type="button" className="btn" disabled={busy === claim.id}
                            onClick={() => decideClaim(claim, 'confirm')}>Confirm</button>
                          <button type="button" className="btn" disabled={busy === claim.id}
                            onClick={() => decideClaim(claim, 'revert')}>Undo</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 3. Unrecognised columns ----------------------------------------- */}
      {!loading && columns.length > 0 && (
        <div className={CARD}>
          <div className={HEAD}>
            <span className={LABEL}>Unrecognised columns</span>
            <span className="text-xs text-avcs-muted">{columns.length} column(s)</span>
          </div>
          <div className="p-4">
            <p className="mb-3 text-xs text-avcs-muted">
              Columns seen on this deal's event types that no rule matched, so their values were dropped. Mapping one
              applies from the next upload; use the field-mapping editor's <strong>re-apply</strong> to fix rows
              already ingested. These mappings are system-wide, not deal-specific.
            </p>
            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
                  <tr>
                    <th className={TH}>Event type</th>
                    <th className={TH}>Column</th>
                    <th className={TH}>Example</th>
                    <th className={TH}>Seen</th>
                    <th className={TH}>Map to</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e6bb812]">
                  {columns.map((field) => (
                    <tr key={field.id} className="hover:bg-[#f8fbff]">
                      <td className="px-3 py-2 text-avcs-muted">{field.event_type}</td>
                      <td className="px-3 py-2 font-medium text-avcs-text">{field.source_field}</td>
                      <td className="px-3 py-2 text-avcs-muted">{fmt(field.sample_value)}</td>
                      <td className="px-3 py-2">{field.occurrence_count}</td>
                      <td className="px-3 py-2">
                        <select
                          className="rounded-lg border border-[#1e6bb833] px-2 py-1"
                          defaultValue=""
                          disabled={busy === field.id}
                          onChange={(e) => mapColumn(field, e.target.value)}
                        >
                          <option value="">Choose a field…</option>
                          {targetFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
